import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { Address, AddressDetails, AddressPlaceType, Country } from '@pedix-workspace/utils';
import { TranslocoDirective, TranslocoService } from '@ngneat/transloco';
import { IconEditComponent } from '@pedix-workspace/angular-ui-icons';
import {
  InputTextComponent,
  InputRadioGroupComponent,
} from '@pedix-workspace/angular-ui-form-reactive';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { MapAddressComponent } from '@pedix-workspace/angular-ui-map';
import {
  AnalyticsService,
  GeoIpService,
  GeoIpState,
} from '@pedix-workspace/pedixapp-core-services';
import { ShippingCostResponse } from '@pedix-workspace/shared-delivery';
import { ShippingCostResponseComponent } from '../shipping-cost-response/shipping-cost-response.component';

export type PlaceTypeOption = { label: string; type: AddressPlaceType };

@Component({
  selector: 'pxw-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss'],
  providers: [],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    InputTextComponent,
    InputRadioGroupComponent,
    FormsModule,
    ButtonComponent,
    IconEditComponent,
    MapAddressComponent,
    ShippingCostResponseComponent,
  ],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class AddressComponent implements OnInit {
  private readonly t = inject(TranslocoService);
  private readonly analytics = inject(AnalyticsService);
  private readonly geoIpService = inject(GeoIpService);

  readonly address = input.required<Address | null>();
  readonly addressChange = output<Address>();

  readonly workerServicesUrl = input.required<string>();
  readonly disableAddressInMap = input.required<boolean | null>();
  readonly isFormSubmitted = input.required<boolean>();
  readonly shippingCostResponse = input.required<ShippingCostResponse | null>();
  readonly calculatingShippingCost = input.required<boolean>();
  readonly countryCodes = input<Country[] | null>();

  readonly geoIpState = signal<GeoIpState | null>(null);
  readonly street = signal<string | null>(null);
  readonly addressDetails = signal<AddressDetails | null>(null);
  readonly placeType = signal<AddressPlaceType | null>(null);
  readonly placeName = signal<string | null>(null);
  readonly lot = signal<string | null>(null);
  readonly floorAndNumber = signal<string | null>(null);
  readonly references = signal<string | null>(null);

  readonly placeTypes = computed<PlaceTypeOption[]>(() => [
    { label: this.t.translate('address.placeTypeHouse'), type: 'house' },
    { label: this.t.translate('address.placeTypeHousingState'), type: 'housing-state' },
    { label: this.t.translate('address.placeTypeBuilding'), type: 'building' },
    { label: this.t.translate('address.placeTypeBusiness'), type: 'business' },
    { label: this.t.translate('address.placeTypeOther'), type: 'other' },
  ]);
  readonly mapInstructions = computed<string[]>(() => [
    this.t.translate('address.mapInstructions1'),
    this.t.translate('address.mapInstructions2'),
    this.t.translate('address.mapInstructions3'),
  ]);
  readonly referencesRequired = computed<boolean>(() => {
    if (this.disableAddressInMap()) {
      return false;
    }
    if (this.placeType() === 'other') {
      return true;
    }
    if (this.placeType() === 'house' && !this.addressDetails()?.streetNumber) {
      return true;
    }
    return false;
  });

  constructor() {
    effect(() => {
      this.addressChange.emit({
        street: this.street(),
        addressDetails: this.addressDetails(),
        placeType: this.placeType(),
        placeName: ['housing-state', 'business', 'building', 'other'].includes(this.placeType()!)
          ? this.placeName()
          : null,
        lot: this.placeType() === 'housing-state' ? this.lot() : null,
        floorAndNumber: this.placeType() === 'building' ? this.floorAndNumber() : null,
        references: this.references(),
      });
    });
  }

  ngOnInit() {
    this.street.set(this.address()?.street || null);
    this.addressDetails.set(this.address()?.addressDetails || null);
    this.placeType.set(this.address()?.placeType || null);
    this.placeName.set(this.address()?.placeName || null);
    this.lot.set(this.address()?.lot || null);
    this.floorAndNumber.set(this.address()?.floorAndNumber || null);
    this.references.set(this.address()?.references || null);

    if (!this.disableAddressInMap()) {
      this.geoIpService.retrieveGeoIpState(this.workerServicesUrl()).then(geoIpState => {
        this.geoIpState.set(geoIpState);
      });
    }
  }

  onUpdateAddressDetails(addressDetails: AddressDetails | null) {
    if (addressDetails) {
      this.analytics.fetchAddressDetails('end-order');
    }
  }
}
